import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput as RNTextInput, View } from 'react-native';
import { Button as Button95, Hourglass, TextInput } from 'react95-native-rabbl';
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import Footer from '@/components/Footer';
import * as AppUtilities from "@/utilities/AppUtilities";
import * as AppUtils from "@/utilities/AppUtils";
import * as AppConstants from "@/utilities/AppConstants";
import * as AppConsts from "@/utilities/AppConsts";
import { useForm, Controller } from "react-hook-form";
import { AppContext } from '@/components/AppContext';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { BasicScrollView } from "@/components/BasicScrollView";
import { AppText } from '@/components/AppText';
import { CustomDivider } from '@/components/CustomDivider';
import { Link } from 'expo-router';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
  },
  formElement: {
    maxWidth: 300,
  },
  inputWrapper: {
    flexDirection: 'row',
  },
});

export default function Contact() {
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const user_info = useSelector((state: RootState) => state.user.user_info);
  const nameInput = useRef<RNTextInput>(null);
  const emailInput = useRef<RNTextInput>(null);
  const detailsInput = useRef<RNTextInput>(null);
  const reduxState = useSelector((state: RootState) => state);

  const { register, control, handleSubmit, setValue, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      details: '',
    }
  });

  useEffect(() => {
    if (user_info &&
        user_info.email &&
        user_info.email != "ccbill@rabbl.date" &&
        user_info.email != "googletest@rabbl.date" &&
        user_info.email != "appletest@rabbl.date"
       ) {
      setValue('email', user_info.email);
    }
  }, [user_info]);

  const onSubmit = useCallback(async (data: any) => {
    try {
      showSpinner();

      let formInput = {};
      (formInput as any)['thoughts'] = data.details;
      (formInput as any)['log'] = AppUtilities.getAccumulatedDebug();
      (formInput as any)['state'] = reduxState;

      await AppUtilities.fetchUnauthorized(
        "Contact.onSubmit",
        "/rabbl/api/contact",
        "POST",
        {
          name: data.name,
          email: data.email,
          details: formInput,
        }
      );

      AppUtils.showInformationalToast('Message sent successfully', "", "done");
      //reset();

    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => onSubmit(data), true);
      AppUtilities.handleException(e, "Contact.onSubmit");
    } finally {
      hideSpinner();
    }
  }, [reduxState]);

  async function onValidationErrors(errors: any) {
    AppUtils.showErrorToast('There are some errors. Review and try again.');
  };

  useEffect(() => {
    if (AppUtilities.shouldAutoFocus()) {
      if (emailInput && emailInput.current) {
        emailInput.current?.focus();
      }
    }
  }, [emailInput]);

  return (
    <BasicScrollView center={false}>
      <Meta title={"Contact us"} />
      <AppText>Send us any thoughts:</AppText>

      <Controller
        control={control}
        name="email"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={emailInput}
            value={value}
            onChangeText={onChange}
            placeholder="Your email"
            style={styles.formElement}
            returnKeyType="next"
            onBlur={onBlur}
            autoComplete="email"
            keyboardType="email-address"
            textContentType="emailAddress"
            autoCapitalize='none'
            onSubmitEditing={() => detailsInput.current?.focus()}
          />
        )}
      />
      {errors.email && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Email is required.</AppText>}

      <Controller
        control={control}
        name="name"
        rules={{
          required: false,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={nameInput}
            value={value}
            onChangeText={onChange}
            placeholder="Your name"
            style={styles.formElement}
            returnKeyType="next"
            onBlur={onBlur}
            autoComplete="name"
            textContentType="name"
            onSubmitEditing={() => emailInput.current?.focus()}
          />
        )}
      />
      {errors.name && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Name is required.</AppText>}

      <Controller
        control={control}
        name="details"
        rules={{
          required: true,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <TextInput
            ref={detailsInput}
            value={value}
            onChangeText={onChange}
            placeholder="Thoughts"
            multiline={true}
            numberOfLines={5}
            returnKeyType="send"
            onBlur={onBlur}
            textAlignVertical="top"
            inputStyle={AppConstants.STYLES.textInputMultiline}
          />
        )}
      />
      {errors.details && <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorRed}>^^ Thoughts required.</AppText>}

      <View style={styles.inputWrapper}>
        <Button95 onPress={handleSubmit(onSubmit, onValidationErrors)}>Send</Button95>
      </View>

      <CustomDivider />

      <CustomText>Direct contact</CustomText>
      <View style={{ gap: 5 }}>
        <AppText>Rabbl Dating, Inc.</AppText>
        <AppText>365 Tolle St</AppText>
        <AppText>New Braunfels, TX 78130</AppText>
        <AppText>830-765-3247</AppText>
        <AppText><Link href="mailto:contact@rabbl.com" style={CustomTextStyles.link}>contact@rabbl.com</Link></AppText>
      </View>
      {
        AppUtilities.isWeb() &&
          <>
            <CustomDivider />
            <CustomText>Billing support</CustomText>
            <AppText>Contact us using the methods above, or contact credit card billing support directly through CCBill: <Link href="https://support.ccbill.com/" style={CustomTextStyles.link} target="_blank">https://support.ccbill.com/</Link></AppText>
          </>
      }
      
      <Footer />
    </BasicScrollView>
  );
}
